import type { InitOptions } from 'i18next';
import { ptPT, enUS, esES, ptBR, frFR } from '@mui/x-data-grid/locales';
import { Localization } from '@mui/x-data-grid/internals';

// map language to country code
export const LANGUAGES_FLAGS: Record<string, string> = {
    ['en']: 'gb',
    ['pt']: 'pt',
    ['es']: 'es',
    ['pt-br']: 'br',
    ['fr']: 'fr',
};
export const FALLBACK_LOCALE = 'en';
export const supportedLocales = ['en', 'pt', 'fr', 'pt-br', 'es'] as const;
export type Locales = (typeof supportedLocales)[number];
export const DEFAULT_NS = 'translation';
export const LANGUAGE_COOKIE = 'language';

/**
 * Our locale codes are lowercase ('pt-br'), but BCP 47 wants an uppercase region subtag ('pt-BR').
 * PostHog requires BCP 47 for `override_display_language`, which drives survey translations.
 *
 * PostHog lowercases both sides before matching, so case is not significant - but the match is
 * otherwise exact, with a one-way fallback from a regional tag to its base language ('pt-PT' finds
 * a 'pt' key, never the reverse). Survey translation keys must therefore use these same codes:
 * a survey keyed 'pt-pt' will NOT be found by our 'pt' and falls back to the base language.
 */
export function toBcp47(locale: string): string {
    const [language, region] = locale.split('-');
    return region ? `${language}-${region.toUpperCase()}` : language;
}

export const MUI_MAP_LOCALES: Record<Locales, Localization> = {
    pt: ptPT,
    es: esES,
    en: enUS,
    'pt-br': ptBR,
    fr: frFR,
};

export function getOptions(ns = DEFAULT_NS): InitOptions {
    return {
        // debug: true, // Set to true to see console logs
        supportedLngs: supportedLocales,
        // Our codes are lowercase ('pt-br'). Without this, i18next normalises 'pt-br' to 'pt-BR', fails
        // to find it in supportedLngs and silently falls back to 'pt', so Brazil gets European Portuguese.
        lowerCaseLng: true,
        fallbackLng: FALLBACK_LOCALE,
        defaultNS: DEFAULT_NS,
        ns,
    };
}
